"use client";

import type {
  AuthApiClient,
  BookingApiClient,
  CreditApiClient,
  EventsApiClient,
  InviteApiClient,
  MapApiClient,
  OrgApiClient,
  ProfileApiClient,
  PublicApiClient,
  QuizApiClient,
  ReferralApiClient,
  TicketApiClient,
  UserRankApiClient
} from "@garther/api-client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, createContext, useContext } from "react";

export interface ApiClients {
  eventsApiClient: EventsApiClient;
  bookingApiClient: BookingApiClient;
  ticketApiClient: TicketApiClient;
  creditApiClient: CreditApiClient;
  profileApiClient: ProfileApiClient;
  quizApiClient: QuizApiClient;
  referralApiClient: ReferralApiClient;
  orgApiClient: OrgApiClient;
  inviteApiClient: InviteApiClient;
  userRankApiClient?: UserRankApiClient;
  mapApiClient?: MapApiClient;
  publicApiClient?: PublicApiClient;
  authApiClient?: AuthApiClient;
}

const ApiClientsContext = createContext<ApiClients | null>(null);

export function ApiClientsProvider({
  queryClient,
  clients,
  children
}: {
  queryClient: QueryClient;
  clients: ApiClients;
  children: ReactNode;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientsContext.Provider value={clients}>{children}</ApiClientsContext.Provider>
    </QueryClientProvider>
  );
}

export function useApiClients(): ApiClients {
  const ctx = useContext(ApiClientsContext);
  if (!ctx) throw new Error("useApiClients must be used inside <ApiClientsProvider>");
  return ctx;
}
